// The bar adds the DEFAULT AVAILABLE VARIANT. Every product in this catalogue is
// single variant, so there is nothing to select and nothing to mirror: the PDP's
// swatch rows link to sibling PRODUCTS, and a swatch click is a full page
// navigation after which this section re-renders server-side. The two-way variant
// mirror this element used to run (its own `<select>` -> the main form's
// `variant-selects`, and `variantChange` back the other way, with an `isUpdating`
// re-entry guard between them) is therefore gone, along with the price rewrite and
// quantity-rule refetch that hung off it. What remains is the reveal observer and
// the published foot-reserve height.
if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
        this.mainProductInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        this.container = this.closest('.sticky-atc-bar');
        this.submitButton = this.querySelector('[type="submit"]');

        this.init();

        const hasRequiredFields = this.validateMainProductRequiredFields();
        if (hasRequiredFields) {
          this.submitButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.productFormActions.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
              this.productFormActions.requestSubmit();
            }, 300);
          });
        }
      }

      validateMainProductRequiredFields() {
        const mainForm = this.mainProductInfo;
        if (!mainForm) return true;
        const requiredFields = mainForm.querySelectorAll('[data-custom-property]');
        return requiredFields.length > 0;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = () => {
          this.checkDevice();
          // P1: the header height changes across the breakpoint — rebuild the
          // observer so its top inset tracks the new --header-height.
          this.createObserver();
        };
        this.checkDevice();

        // E2 (brief §6): re-measure the published height whenever the bar's
        // box changes (wrapped title, font load, resize) — the media-query
        // flip alone misses those.
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.checkDevice());
          this.resizeObserver.observe(this.container);
        }

        this.createObserver();
      }

      createObserver() {
        if (this.observer) this.observer.disconnect();
        // P1/D10 (brief §9): the top inset is the sticky header's occluded
        // strip. header.js publishes --header-height on :root; read it rather
        // than the old hard-coded -80px so the bar reveals exactly when the
        // buy box slips under the pinned header.
        const headerHeight =
          getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim() || '80px';
        const rootMargin = `-${headerHeight} 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const method = entry.intersectionRatio !== 1 ? 'add' : 'remove';
              this.container.classList[method]('sticky-atc-bar--show');
            });
          },
          { threshold: 1, rootMargin }
        );
        this.setObserveTarget();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      setObserveTarget() {
        this.observer.observe(this.productFormActions);
        this.observeTarget = this.productFormActions;
      }

      checkDevice(e) {
        // E1 (brief §6): measure the CONTAINER (padded + bordered .sticky-atc-bar),
        // not this inner row — the old value under-reported the foot reserve.
        const target = this.container || this;
        document.documentElement.style.setProperty('--sticky-atc-bar-height', target.offsetHeight + 'px');
      }

    }
  );
}
